import * as React from "react";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
  StackLayout,
} from "@progress/kendo-react-layout";
import { CPText } from "shared/components/base";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import CPButton from "shared/components/shared/CPButton/CPButton";
import { useSendTestEmailMutation } from "data/admin/admin";
import useAppStore from "shared/state/useAppState";
import "./AdminTestsPageStyles.scss";

const EMAIL_TYPES = [
  { text: "Pending Access Request (Admin)", value: "new-access-request" },
  {
    text: "Pending Access Request (Agency Manager)",
    value: "agency-manager-access-request",
  },
  { text: "Access Granted (SSO)", value: "access-granted-sso" },
  { text: "Access Granted (Passwordless)", value: "access-granted-passwordless" },
  { text: "Short Code", value: "short-code" },
];

const AdminTestsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [emailType, setEmailType] = React.useState<string>(EMAIL_TYPES[0].value);
  const mutation = useSendTestEmailMutation();
  const { setToastMsg } = useAppStore();

  const handleTabSelect = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  const handleSend = async () => {
    if (!email || emailError) return;
    try {
      await mutation.mutateAsync({ emailType, toEmail: email });
      setToastMsg(`Test email sent to ${email}.`, "success");
    } catch {
      setToastMsg("Failed to send test email.", "error");
    }
  };

  const canSend = !!email && !emailError;

  return (
    <div className="admin-tests-page">
      <CPText
        font="montserrat"
        style={{ fontWeight: 700, fontSize: 18, display: "block", marginBottom: 16 }}
      >
        Admin Tests
      </CPText>
      <TabStrip selected={selectedTab} onSelect={handleTabSelect} animation={false}>
        <TabStripTab title="Email">
          <StackLayout
            orientation="vertical"
            gap={16}
            style={{ padding: "20px 0", maxWidth: 480 }}
          >
            <CPText font="montserrat" style={{ fontWeight: 600, fontSize: 13 }}>
              Send a test email using one of the configured templates to verify
              delivery and formatting in the current environment.
            </CPText>
            <CPEmailTextBox
              name="toEmail"
              label="Send to"
              value={email}
              onChange={(e) => {
                setEmail(e.value as string);
                setEmailError(e.isValid ? undefined : e.error);
              }}
              errorMsg={emailError}
            />
            <CPDropDownList
              name="emailType"
              label="Email Template"
              data={EMAIL_TYPES}
              textField="text"
              dataItemKey="value"
              valueKey="value"
              value={emailType}
              onChange={(e) => setEmailType(e.value as string)}
            />
            <div>
              <CPButton
                themeColor="primary"
                showLoader={mutation.isLoading}
                onClick={handleSend}
                disabled={!canSend}
              >
                Send Test Email
              </CPButton>
            </div>
          </StackLayout>
        </TabStripTab>
      </TabStrip>
    </div>
  );
};

export default AdminTestsPage;
